import React, { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Terminal, Database, Send, History, Search, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface DeveloperConsoleLayoutProps {
  explorer: ReactNode;
  studio: ReactNode;
  onSearchChange?: (query: string) => void;
}

export function DeveloperConsoleLayout({
  explorer,
  studio,
  onSearchChange
}: DeveloperConsoleLayoutProps) {
  return (
    <div className="flex h-full flex-col bg-background overflow-hidden">
      {/* Console Header */}
      <header className="flex h-14 shrink-0 items-center justify-between border-b bg-card px-4 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded bg-primary/10 text-primary">
            <Terminal className="h-4 w-4" />
          </div>
          <div>
            <h1 className="text-sm font-bold tracking-tight">API Lab</h1>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Developer Console</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" className="h-8 text-xs gap-2">
            <History className="h-3.5 w-3.5" />
            History
          </Button>
          <div className="h-4 w-px bg-border mx-1" />
          <div className="relative w-64">
            <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground/50" />
            <Input 
              placeholder="Filter endpoints..." 
              className="h-8 pl-8 text-xs bg-muted/30 border-none focus-visible:ring-primary/20"
              onChange={(e) => onSearchChange?.(e.target.value)}
            />
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* API Explorer (Left) */}
        <aside className="w-72 shrink-0 border-r bg-muted/20 overflow-y-auto">
          {explorer}
        </aside>

        {/* Request Studio (Center) */}
        <main className="flex-1 flex flex-col min-w-0 overflow-hidden bg-background">
          <div className="flex-1 overflow-y-auto p-4 lg:p-6">
            {studio}
          </div>
        </main>
      </div>
    </div>
  );
}
